import { API_BASE_URL, enforceAuthResponse } from "../client";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

export type LmsOverdueUploadResult = Record<string, unknown>;

export class LmsOverdueUploadError extends Error {
  readonly body: unknown;

  constructor(message: string, body: unknown) {
    super(message);
    this.name = "LmsOverdueUploadError";
    this.body = body;
  }
}

function messageFromBody(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;
  const record = body as Record<string, unknown>;
  const detail = record.detail;

  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (!item || typeof item !== "object") return "";
        const row = item as { msg?: unknown; loc?: unknown };
        const msg = row.msg != null ? String(row.msg) : "";
        const loc = Array.isArray(row.loc) ? row.loc.map(String).join(".") : "";
        return [loc, msg].filter(Boolean).join(": ");
      })
      .filter(Boolean);
    if (parts.length > 1) return `${parts.length} validation errors. See the list below.`;
    return parts[0] || fallback;
  }
  if (detail && typeof detail === "object") {
    const nested = detail as Record<string, unknown>;
    if (typeof nested.message === "string" && nested.message.trim()) return nested.message;
    if (typeof nested.error === "string" && nested.error.trim()) return nested.error;
  }
  if (typeof record.message === "string" && record.message.trim()) return record.message;
  if (typeof record.error === "string" && record.error.trim()) return record.error;
  return fallback;
}

export class LmsOverdueService {
  static isAcceptedFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
  }

  /** POST /api/v1/bulk-import/lms-overdue-amount — multipart file updates loan_details. */
  static async uploadLmsOverdueAmount(file: File): Promise<LmsOverdueUploadResult> {
    if (!this.isAcceptedFile(file)) {
      throw new Error("Upload an Excel or CSV file (.xlsx, .xls, or .csv).");
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error("File is too large (max 20 MB).");
    }

    const form = new FormData();
    form.append("file", file);

    const token = localStorage.getItem("access_token");
    const response = await fetch(`${API_BASE_URL}/bulk-import/lms-overdue-amount`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });

    enforceAuthResponse(response);

    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new LmsOverdueUploadError(
        messageFromBody(body, `Upload failed (HTTP ${response.status})`),
        body,
      );
    }

    if (body && typeof body === "object" && !Array.isArray(body)) {
      return body as LmsOverdueUploadResult;
    }
    return { result: body };
  }
}
